import { useCallback, useEffect, useRef, useState } from 'react';
import { union } from 'lodash';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from '../context';
import { isSyncingCalEventsAtom, visibleCalendarIdsAtom } from '../recoil';
import {
  useSyncAppleCalendarsList,
  useSyncGoogleCalendarEvents,
  useSyncOutlookCalendarEvents,
  useSubscribeGoogleCalendarWatch,
  useSubscribeOutlookCalendarWatch,
} from '../mutations';
import { calendarApi } from '../api';
import { useCalendarsMeta } from '../queries';
import { fetchAppleCalendarsFromDesktop, isAppleCalendarDesktopSupported } from '../../helpers';

export const useSyncCalEventsApi = () => {
  const alreadySyncedWeeks = useRef<string[]>([]);
  const hasSubscribed = useRef<boolean>(false);
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [isSyncingLifestackEvents, setIsSyncingLifestackEvents] = useState(false);
  const [_, setIsSyncingCalEvents] = useRecoilState(isSyncingCalEventsAtom);
  const visibleCalendarIds = useRecoilValue(visibleCalendarIdsAtom);

  const { syncGoogleCalEvents, isPending: isSyncingGoogleEvents } = useSyncGoogleCalendarEvents();
  const { syncOutlookCalEvents, isPending: isSyncingOutlookEvents } =
    useSyncOutlookCalendarEvents();
  const { syncAppleCalendarsList, isPending: isSyncingAppleEvents } = useSyncAppleCalendarsList();
  const { subscribeGoogleCalendarWatch } = useSubscribeGoogleCalendarWatch();
  const { subscribeOutlookCalendarWatch } = useSubscribeOutlookCalendarWatch();
  const { calendarsMeta } = useCalendarsMeta({ userId: user.userId });

  useEffect(() => {
    setIsSyncingCalEvents(
      isSyncingGoogleEvents ||
        isSyncingOutlookEvents ||
        isSyncingAppleEvents ||
        isSyncingLifestackEvents
    );
  }, [
    isSyncingGoogleEvents,
    isSyncingOutlookEvents,
    isSyncingAppleEvents,
    isSyncingLifestackEvents,
  ]);

  // Helper function to group calendar IDs by provider type
  const groupCalendarsByProvider = useCallback(
    (calendarIds: string[]) => {
      const googleCalendars: string[] = [];
      const outlookCalendars: string[] = [];
      const appleCalendars: string[] = [];
      const otherCalendars: string[] = [];

      calendarIds.forEach((calendarId) => {
        const calendar = calendarsMeta.find((cal) => cal.calendarId === calendarId);
        if (calendar) {
          if (calendar.calendarType === 'google') {
            googleCalendars.push(calendarId);
          } else if (calendar.calendarType === 'outlook') {
            outlookCalendars.push(calendarId);
          } else if (calendar.calendarType === 'apple') {
            appleCalendars.push(calendarId);
          } else {
            otherCalendars.push(calendarId);
          }
        }
      });

      return { googleCalendars, outlookCalendars, appleCalendars, otherCalendars };
    },
    [calendarsMeta]
  );

  const syncLifestackCalendars = useCallback(
    async ({
      calendarIds,
      startTime,
      endTime,
      refetchCount = 1,
    }: {
      calendarIds: string[];
      startTime: string;
      endTime: string;
      refetchCount?: number;
    }) => {
      if (!calendarIds.length || !user.userId) {
        return;
      }

      setIsSyncingLifestackEvents(true);
      try {
        await calendarApi.syncCalendarEvents(user.userId, startTime, endTime, calendarIds);

        if (refetchCount === 1) {
          queryClient.invalidateQueries({
            queryKey: ['lifestack.calendar.events', { startDate: startTime, endDate: endTime }],
          });
        }
      } finally {
        setIsSyncingLifestackEvents(false);
      }
    },
    [queryClient, user.userId]
  );

  // Helper function to subscribe to calendar watches
  const subscribeToCalendarWatches = useCallback(
    (calendarIds: string[] = visibleCalendarIds) => {
      if (!calendarIds.length || !user.userId || hasSubscribed.current) {
        return;
      }

      const { googleCalendars, outlookCalendars } = groupCalendarsByProvider(calendarIds);

      // Subscribe to Google calendar watches
      if (googleCalendars.length > 0) {
        subscribeGoogleCalendarWatch({
          calendarIds: googleCalendars,
          userId: user.userId,
        });
        hasSubscribed.current = true;
      }

      // Subscribe to Outlook calendar watches
      if (outlookCalendars.length > 0) {
        subscribeOutlookCalendarWatch({
          calendarIds: outlookCalendars,
          userId: user.userId,
        });
        hasSubscribed.current = true;
      }
    },
    [groupCalendarsByProvider, user.userId, visibleCalendarIds]
  );

  const syncAllCalendarEvents = useCallback(
    async (
      startTime: string,
      endTime: string,
      targetCalendarIds: string[] = visibleCalendarIds
    ) => {
      if (
        alreadySyncedWeeks.current.includes(startTime) ||
        !targetCalendarIds?.length ||
        !user.userId
      ) {
        return;
      }

      const { googleCalendars, outlookCalendars, appleCalendars, otherCalendars } =
        groupCalendarsByProvider(targetCalendarIds);

      let didSync = false;

      // Sync Google calendars using the Google-specific endpoint
      if (googleCalendars.length > 0) {
        syncGoogleCalEvents({
          userId: user.userId,
          calendarIds: googleCalendars,
          startTime,
          endTime,
        });
        didSync = true;
      }

      // Sync Outlook calendars using the Outlook-specific endpoint
      if (outlookCalendars.length > 0) {
        syncOutlookCalEvents({
          userId: user.userId,
          calendarIds: outlookCalendars,
          startTime,
          endTime,
        });
        didSync = true;
      }

      if (appleCalendars.length > 0 && isAppleCalendarDesktopSupported()) {
        fetchAppleCalendarsFromDesktop({ startTime, endTime })
          .then((appleCalendarData) =>
            syncAppleCalendarsList({
              userId: user.userId,
              email: user.email,
              metricId: user.metricId,
              userPersona: user.persona?.job,
              startTime,
              endTime,
              shouldSyncDeletedList: true,
              appleCalendars: appleCalendarData,
            })
          )
          .catch(() => void 0);
        didSync = true;
      } else if (appleCalendars.length > 0) {
        // Native bridge unavailable (web/non-macOS): fetch events already stored in the
        // backend (e.g. synced from the mobile app) so they still appear on desktop.
        void syncLifestackCalendars({ calendarIds: appleCalendars, startTime, endTime });
        didSync = true;
      }

      if (otherCalendars.length > 0) {
        void syncLifestackCalendars({
          calendarIds: otherCalendars,
          startTime,
          endTime,
        });
        didSync = true;
      }

      if (didSync) {
        alreadySyncedWeeks.current = union(alreadySyncedWeeks.current, [startTime]);
      }
    },
    [
      user.userId,
      user.email,
      user.metricId,
      user.persona?.job,
      visibleCalendarIds,
      groupCalendarsByProvider,
      syncLifestackCalendars,
      syncGoogleCalEvents,
      syncOutlookCalEvents,
      syncAppleCalendarsList,
    ]
  );

  const reSyncCalEvents = useCallback(
    async (calendarIds: string[], startTime: string, endTime: string) => {
      const { googleCalendars, outlookCalendars, appleCalendars, otherCalendars } =
        groupCalendarsByProvider(calendarIds);
      // Create sync promises for parallel execution
      const syncPromises: Promise<any>[] = [];

      // Sync Google calendars using the Google-specific endpoint
      if (googleCalendars.length > 0) {
        syncPromises.push(
          syncGoogleCalEvents({
            userId: user.userId,
            calendarIds: googleCalendars,
            startTime,
            endTime,
            refetchCount: 1,
          })
        );
      }

      // Sync Outlook calendars using the Outlook-specific endpoint
      if (outlookCalendars.length > 0) {
        syncPromises.push(
          syncOutlookCalEvents({
            userId: user.userId,
            calendarIds: outlookCalendars,
            startTime,
            endTime,
            refetchCount: 1,
          })
        );
      }

      if (appleCalendars.length > 0 && isAppleCalendarDesktopSupported()) {
        syncPromises.push(
          fetchAppleCalendarsFromDesktop({ startTime, endTime }).then((appleCalendarData) =>
            syncAppleCalendarsList({
              userId: user.userId,
              email: user.email,
              metricId: user.metricId,
              userPersona: user.persona?.job,
              startTime,
              endTime,
              shouldSyncDeletedList: true,
              appleCalendars: appleCalendarData,
            })
          )
        );
      } else if (appleCalendars.length > 0) {
        // Native bridge unavailable: read events already stored in the backend.
        syncPromises.push(
          syncLifestackCalendars({ calendarIds: appleCalendars, startTime, endTime, refetchCount: 1 })
        );
      }

      if (otherCalendars.length > 0) {
        syncPromises.push(
          syncLifestackCalendars({
            calendarIds: otherCalendars,
            startTime,
            endTime,
            refetchCount: 1,
          })
        );
      }

      // Execute all sync operations in parallel
      if (syncPromises.length > 0) {
        await Promise.all(syncPromises);
        alreadySyncedWeeks.current = union(alreadySyncedWeeks.current, [startTime]);
      }
    },
    [
      user,
      groupCalendarsByProvider,
      syncLifestackCalendars,
      syncGoogleCalEvents,
      syncOutlookCalEvents,
      syncAppleCalendarsList,
    ]
  );

  return { syncAllCalendarEvents, reSyncCalEvents, subscribeToCalendarWatches };
};
